
import { Component, ChangeDetectionStrategy, Input, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TableModule } from 'src/app/table/table.module';
import { TRANSLATION_MAP } from 'src/app/enums';
import { TABLE_DEFAULT_ITEMS_PER_PAGE, TableComponent } from 'src/app/table/table.component';
import { BehaviorSubject } from 'rxjs';
import { IRowActionGetter, IRowClassNamesGetter, ITableData } from 'src/app/table/interfaces';
import { MULTI_REGISTRATION_ATTRIBUTE_NAMES, TableRowObject, DraftSubParticipantRecord } from './types';
import {
    MultiRegistrationConfig,
    TicketOption,
    UIParticipant,
    UIParticipantStatus,
    UIState,
} from '@azavista/participants/dist/event-app/multiregistration/';
import { getMapByGetter } from '../utils.service';
import { SharedService } from '../shared.service';
import { MultiRegistrationFormComponent } from './multi-registration-form.component';
import { IField, IObjectWithId } from '../interfaces';
import { MultiRegistrationFormService } from './multi-registration-form.service';
import { getIsRequireTicket } from './utils';
import { Product } from '@azavista/payments';

@Component({
    selector: 'aza-rws-multi-registration-form-table',
    imports: [TranslateModule, TableModule],
    templateUrl: `./multi-registration-form.table.component.html`,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiRegistrationFormTableComponent
    implements
        Pick<
            TableComponent<TableRowObject>,
            'extraRowDivTemplate' | 'rowActionGetter'
        >,
        Pick<
            MultiRegistrationFormComponent,
            'config' | 'state' | 'subParticipantFields' | 'allParticipantFields'
        >
{
    private _state: UIState;
    public get state(): UIState {
        return this._state;
    }

    @Input({
        required: true,
    })
    public set state(value: UIState) {
        this._state = value;
        this.loadTableData();
    }

    @Input({
        required: true,
    })
    extraRowDivTemplate?: TableComponent['extraRowDivTemplate'];

    @Input({
        required: true,
    })
    rowActionGetter: IRowActionGetter<TableRowObject>;

    @Input({
        required: true,
    })
    config: MultiRegistrationConfig;

    @Input({
        required: true,
    })
    products: Product[];

    @Input({
        required: true,
    })
    eventParticipant?: IObjectWithId;

    @Input()
    allParticipantFields: IField[] | undefined;

    private _subParticipantFields: IField[] = [];
    public get subParticipantFields(): IField[] {
        return this._subParticipantFields;
    }

    @Input({
        required: true,
    })
    public set subParticipantFields(value: IField[]) {
        this._subParticipantFields = value;
        this.loadTableData();
    }

    @Input({
        required: true,
    })
    editedSubParticipantRecord: DraftSubParticipantRecord;

    data$ = new BehaviorSubject<ITableData<TableRowObject>>({
        pagedResponse: {
            limit: 0,
            object_count: 0,
            offset: 0,
        },
        rowObjects: [],
    });

    private _tableComponentElement?: TableComponent;
    public get tableComponentElement(): TableComponent {
        return this._tableComponentElement;
    }

    @ViewChild(TableComponent)
    public set tableComponentElement(value: TableComponent) {
        this._tableComponentElement = value;
        this.loadTableData();
    }

    TRANSLATION_MAP = TRANSLATION_MAP;

    constructor(
        private sharedSvc: SharedService,
        private formSvc: MultiRegistrationFormService,
    ) {}

    loadTableData(offset?: number) {
        const data = this.getTableDataData$params(offset);
        if (data?.rowObjects.length === 0 && data?.pagedResponse.offset > 0) {
            this.tableComponentElement?.onPrevPageRequested();
            return;
        }
        if (data) {
            this.data$.next(data);
        }
    }

    private getTableDataData$params(offset?: number): ITableData<TableRowObject> | undefined {
        if (!this.state || !this.products) {
            return undefined;
        }
        offset ??= this.tableComponentElement?.tableSettings?.page.offset || 0;
        const pageLimit = this.getTablePageLimit();
        const getRowObjectItem = (
            participant: UIParticipant
        ): TableRowObject => ({
            ...participant.participant_attributes,
            ...participant,
            ui_participant: participant,
            [MULTI_REGISTRATION_ATTRIBUTE_NAMES.participant_addons_field_id]: participant.addons,
        });
        const allSubParticipants: UIParticipant[] = [];
        if (this.state.main_participant.ticket_type_id) {
            const main_participant: UIParticipant = {
                ...this.state.main_participant,
            };
            main_participant.participant_attributes = {
                ...this.sharedSvc.clone(this.eventParticipant || {}),
                ...main_participant.participant_attributes,
            };
            allSubParticipants.push(main_participant);
        }
        allSubParticipants.push(
            ...this.state.subparticipants
        );
        let rowObjects = allSubParticipants.slice(offset, offset + pageLimit).map(getRowObjectItem);

        const columns: IField[] = [];
        const crmStatusField = this.allParticipantFields?.find((field) => field.name === 'crmstatus');

        if (crmStatusField) {
            columns.push({
                ...crmStatusField,
                editable: false
            } as IField);
        }
        columns.push(...this.subParticipantFields);

        if (getIsRequireTicket(this.config)) {
            const allTicketOptions: TicketOption[] = [];
            rowObjects.forEach((rowParticipant) => {
                const allTicketOptionsMap = getMapByGetter(
                    allTicketOptions,
                    (opt) => opt.value
                );
                rowParticipant.ui_participant.ticket_options.forEach((opt) => {
                    if (!allTicketOptions[opt.value]) {
                        allTicketOptions.push(opt);
                    }
                });
            });
            columns.push(
                this.formSvc.createTicketField({
                    ticket_options: allTicketOptions,
                    status: UIParticipantStatus.PENDING_ASSIGNMENT,
                    ticket_type_id: null,
                    editable: true,
                }, this.products)
            );
            if (rowObjects.some(row => row.ui_participant.addons?.length)) {
                columns.push(
                    this.formSvc.createAddonsField(
                        this.products.map(product => ({
                            label: product.name,
                            value: product.id,
                        }))
                    )
                );
            }
        }
        return {
            columns,
            rowObjects,
            pagedResponse: {
                limit: pageLimit,
                object_count: allSubParticipants.length,
                offset,
            },
        };
    }

    rowClassNamesGetter: IRowClassNamesGetter<TableRowObject> = (row) => {
        const ui_participant = row.ui_participant;
        const classNames: string[] = [`row-id--${row.id}`, `row-status--${ui_participant.status}`];
        if (ui_participant.is_main) {
            classNames.push('row-main-participant');
        }
        if (this.editedSubParticipantRecord[row.id] || ui_participant.error?.code) {
            classNames.push('aza-table-table-view-body-row--border-none');
        }
        return classNames;
    };

    private getTablePageLimit(): number {
        return this.tableComponentElement?.tableSettings?.page?.limit || TABLE_DEFAULT_ITEMS_PER_PAGE;
    }
}
